import React, { useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Location,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import { loadAllContacts } from '@disco/state-management/actions/contact-actions'
import { loadEmailGroups } from '@disco/state-management/actions/EmailGroupActions'
import {
  getBrief,
  loadBrief,
  saveBrief,
  unloadBrief,
} from '@disco/state-management/reducers/entities/briefs'
import { getContactsAll } from '@disco/state-management/selectors/contacts-all'

import type { Brief } from 'types/brief'
import type { State } from 'types/redux'

import { Button, Dialog, Flex } from 'components/ds'
import Spinner from 'components/ui/spinner'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { RootState } from 'store/configureStore'

const mapStateToProps = (
  state: RootState,
  ownProps: { params: { briefId: string } },
) => ({
  brief: getBrief(state, { briefId: parseInt(ownProps.params.briefId) }),
})

const mapDispatchToProps = {
  saveBrief,
  unloadBrief,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type OwnProps = {
  children?: React.ReactNode
  params: {
    briefId: string
  }
  router: any
  route: any
}

type Props = PropsFromRedux & OwnProps

const LeavePageDialog = ({
  brief,
  router,
  nextLocation,
  onClose,
}: {
  brief: Brief
  router: any
  nextLocation: string
  onClose: (saving: boolean) => void
}) => {
  const dispatch = useDispatch()
  return (
    <Dialog.Root defaultOpen>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>Save brief</Dialog.Title>
        <Dialog.Description>
          Do you want to save your campaign and continue work later?
        </Dialog.Description>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              onClick={async () => {
                await onClose(false)
                router.transitionTo(nextLocation)
              }}
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Dialog.Close
            onClick={async () => {
              await onClose(true)
              dispatch(saveBrief(brief))
              router.transitionTo(nextLocation)
            }}
          >
            <Button>Save and close</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

class BriefsPageWrapper extends React.Component<Props> {
  state = {
    showLeavePageDialog: false,
    nextLocation: null,
    saving: false,
  }

  onClose = (saving: boolean): Promise<void> => {
    return new Promise(resolve => {
      this.setState({ saving }, () => {
        resolve()
      })
    })
  }

  // TODO: use useBlocker in v7 instead of router.setRouteLeaveHook
  // https://reactrouter.com/en/main/hooks/use-blocker
  // doesn't work in v6 with BrowserRouter https://reactrouter.com/6.30.1/hooks/use-blocker
  /*
  _routerWillLeave = (nextLocation: Location) => {
    // we can't do anything if session is expired
    if (
      nextLocation.pathname === '/logout' &&
      nextLocation.query.sessionExpired === '1'
    )
      return true

    if (this.state.showLeavePageDialog) {
      if (this.props.brief?.dirty && !this.state.saving) {
        // unload the brief before leaving the page if it was dirty to not display dirty changes when user comes back
        this.props.unloadBrief(this.props.brief)
      }
      // if we are already showing the dialog, and user is leaving, let them leave
      return true
    }

    if (this.props.brief?.dirty && !this.props.brief?.saving) {
      if (
        this.props.brief?.status === 'sent' ||
        this.props.brief?.sent_status
      ) {
        this.setState({ showLeavePageDialog: true, nextLocation })
        return false
      } else {
        // Save the brief before leaving the page
        this.props.saveBrief(this.props.brief)
        return true
      }
    }

    return true
  }

  componentDidMount() {
    this.props.router.setRouteLeaveHook(this.props.route, this._routerWillLeave)
  }
  */

  render() {
    return (
      <>
        {this.props.children}
        {this.state.showLeavePageDialog && (
          <LeavePageDialog
            brief={this.props.brief}
            router={this.props.router}
            nextLocation={this.state.nextLocation}
            onClose={this.onClose}
          />
        )}
      </>
    )
  }
}

const BriefsPageComponent = (): React.ReactElement<'div'> => {
  const { briefId } = useParams<{ briefId: string }>()
  const { loading, loaded } = useSelector(getContactsAll)
  const dispatch = useDispatch()
  useEffect(() => {
    if (!loaded && !loading) {
      dispatch(loadAllContacts())
    }
    dispatch(loadEmailGroups())
  }, [])
  useEffect(() => {
    dispatch(loadBrief(parseInt(briefId!)))
  }, [briefId])

  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(briefId!) }),
  )
  if (!brief) return <Spinner />

  return (
    <div className="Page">
      <Outlet />
    </div>
  )
}

const ConnectedBriefsPageWrapper = connector(BriefsPageWrapper)

// Wrapper component to provide router props to the class component
const BriefsPageWithRouter = (props: any) => {
  const params = useParams<{ briefId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Create a router object that mimics the old react-router v3/v4 API
  const router = {
    push: navigate,
    replace: (path: string) => navigate(path, { replace: true }),
    setRouteLeaveHook: () => {}, // Simplified for now
    transitionTo: navigate,
  }

  // Create a route object (simplified)
  const route = {}

  return (
    <ConnectedBriefsPageWrapper
      {...props}
      params={params}
      router={router}
      route={route}
    >
      <BriefsPageComponent />
    </ConnectedBriefsPageWrapper>
  )
}

export const BriefsPage = BriefsPageWithRouter
